// src/services/apiService.ts
import axios, { AxiosInstance } from 'axios';
import authService from './authService';
import { API_BASE_URL, API_PATH } from '../config/api.config';

// Alkalmazás típus interfész
export interface Application {
  id: string;
  name: string;
  description: string;
  url: string;
  status: 'active' | 'inactive';
  icon?: string;
  type: 'test' | 'demo';
  created_at: string;
  updated_at: string;
}

// Felhasználó interfész
export interface User {
  created_at: any;
  id: number;
  username: string;
  email?: string;
  full_name?: string;
  disabled: boolean;
  role: string;
  default_app?: string;
}

class ApiService {
  private apiClient: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
    this.apiClient = this.createClient(`${this.baseURL}${API_PATH}`);
  }

  private createClient(baseURL: string): AxiosInstance {
    const client = axios.create({ baseURL, withCredentials: true });

    // Interceptor a JWT token hozzáadásához
    client.interceptors.request.use((config) => {
      const authHeader = authService.getAuthHeader();
      if (config.headers && authHeader.Authorization) {
        config.headers.set('Authorization', authHeader.Authorization);
      }
      return config;
    });

    // Interceptor a token lejárat kezelésére
    client.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
    
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          console.log(`INTERCEPTOR: Caught 401 for request: ${originalRequest.url}`, error.response.data);
    
          // Kiterjesztett állapot ellenőrzés a login oldallal kapcsolatban
          const isOnLoginPage = window.location.pathname === '/login';
          const urlParams = new URLSearchParams(window.location.search);
          const isForcedLogout = urlParams.get('force_logout') === 'true';
          const hasRedirect = urlParams.has('redirect');
          const fromTestPulse = urlParams.get('source') === 'testpulse';
          
          console.log(`INTERCEPTOR: State - isOnLoginPage: ${isOnLoginPage}, isForcedLogout: ${isForcedLogout}, hasRedirect: ${hasRedirect}, fromTestPulse: ${fromTestPulse}`);
    
          // Kibővített feltételrendszer - NE regeneráljunk tokent, ha a login oldalon vagyunk kijelentkezés után
          if (isOnLoginPage && (isForcedLogout || hasRedirect || fromTestPulse)) {
            console.warn("INTERCEPTOR: Detected 401 on login page with logout indicators. Aborting all recovery attempts.");
            console.log("INTERCEPTOR: URL params:", window.location.search);
            return Promise.reject(error); // Ne próbálkozzunk semmilyen helyreállítással
          }
    
          try {
            // A többi kód változatlan
            // Ha app_access_token miatt 401-es hiba (ÉS NEM a fenti kizáró eset)
            if (error.response.data &&
                (error.response.data.detail === "App token not found" ||
                 error.response.data.detail === "Invalid token")) {
    
               console.log('INTERCEPTOR: Condition met for app token regeneration (not excluded case).');
               console.log('INTERCEPTOR: Attempting to regenerate app token after 401 error.');
               await authService.generateAppToken();
               console.log('INTERCEPTOR: App token regeneration attempted. Retrying original request.');
               return axios(originalRequest); // Kérés újrapróbálása
            }
    
            // JWT token frissítése (ez a logika maradhat, bár login oldalon valószínűleg nincs JWT)
            console.log('INTERCEPTOR: Attempting JWT refresh.');
            const refreshed = await authService.refreshTokenIfNeeded();
            if (refreshed) {
              console.log('INTERCEPTOR: JWT refresh successful. Retrying original request.');
              originalRequest.headers['Authorization'] = authService.getAuthHeader().Authorization;
              return axios(originalRequest);
            } else {
              console.log('INTERCEPTOR: JWT refresh failed.');
            }
          } catch (refreshError) {
            console.error('INTERCEPTOR: Error during token refresh/regeneration:', refreshError);
            // Ha nem sikerül a token frissítése, akkor kijelentkeztetünk (ez jó lehet)
            authService.logout(); // Csak localStorage törlés
            // Ne irányítsunk át innen automatikusan, hagyjuk a komponenst kezelni
            // window.location.href = '/login?error=session_expired';
            return Promise.reject(refreshError); // Dobjuk tovább a hibát
          }
        }
    
        // Ha nem 401 vagy már próbáltuk (_retry), csak dobja tovább a hibát
        return Promise.reject(error);
      }
    );

    return client;
  }

  // ========== Alkalmazás kezelő API metódusok ==========

  // Alkalmazások lekérdezése
  async getApplications(params?: { status?: string; type?: string }) {
    try {
      const response = await this.apiClient.get('/applications', { params });
      return response.data.applications || [];
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  }

  // Egy alkalmazás részleteinek lekérdezése
  async getApplication(appId: string) {
    try {
      const response = await this.apiClient.get(`/applications/${appId}`);
      return response.data.application;
    } catch (error) {
      console.error(`Error fetching application ${appId}:`, error);
      throw error;
    }
  }

  // Új alkalmazás létrehozása
  async createApplication(appData: Omit<Application, 'id' | 'created_at' | 'updated_at'>) {
    try {
      const response = await this.apiClient.post('/applications', appData);
      return response.data.application;
    } catch (error) {
      console.error('Error creating application:', error);
      throw error;
    }
  }

  // Alkalmazás módosítása
  async updateApplication(appId: string, appData: Partial<Omit<Application, 'id' | 'created_at' | 'updated_at'>>) {
    try {
      const response = await this.apiClient.put(`/applications/${appId}`, appData);
      return response.data.application;
    } catch (error) {
      console.error(`Error updating application ${appId}:`, error);
      throw error;
    }
  }

  // Alkalmazás törlése
  async deleteApplication(appId: string) {
    try {
      const response = await this.apiClient.delete(`/applications/${appId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting application ${appId}:`, error);
      throw error;
    }
  }

  // Alkalmazás indítása - beállítja az alkalmazás státuszát aktívra
  async startApplication(appId: string) {
    try {
      const response = await this.apiClient.post(`/applications/${appId}/start`);
      return response.data.application;
    } catch (error) {
      console.error(`Error starting application ${appId}:`, error);
      throw error;
    }
  }

  // Alkalmazás leállítása - beállítja az alkalmazás státuszát inaktívra
  async stopApplication(appId: string) {
    try {
      const response = await this.apiClient.post(`/applications/${appId}/stop`);
      return response.data.application;
    } catch (error) {
      console.error(`Error stopping application ${appId}:`, error);
      throw error;
    }
  }

  // ========== Felhasználó kezelő API metódusok ==========

  // Felhasználók lekérdezése
  async getUsers(params?: { status?: string; role?: string }) {
    try {
      const response = await this.apiClient.get('/users', { params });
      return response.data.users || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Egy felhasználó részleteinek lekérdezése
  async getUser(userId: number) {
    try {
      const response = await this.apiClient.get(`/users/${userId}`);
      return response.data.user;
    } catch (error) {
      console.error(`Error fetching user ${userId}:`, error);
      throw error;
    }
  }

  // Új felhasználó létrehozása
  async createUser(userData: {
    username: string;
    password: string;
    email?: string;
    full_name?: string;
    role?: string;
    disabled?: boolean;
    default_app?: string;
  }) {
    try {
      const response = await this.apiClient.post('/users', userData);
      return response.data.user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  // Felhasználó módosítása
  async updateUser(userId: number, userData: {
    email?: string;
    full_name?: string;
    password?: string;
    role?: string;
    disabled?: boolean;
    default_app?: string;
  }) {
    try {
      const response = await this.apiClient.put(`/users/${userId}`, userData);
      return response.data.user;
    } catch (error) {
      console.error(`Error updating user ${userId}:`, error);
      throw error;
    }
  }

  // Felhasználó törlése (deaktiválása)
  async deleteUser(userId: number) {
    try {
      const response = await this.apiClient.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting user ${userId}:`, error);
      throw error;
    }
  }

  // ========== Rendszer információ API metódusok ==========

  // Rendszer állapot lekérdezése (CPU, memória, háttértár, stb.)
  async getSystemStatus() {
    try {
      const response = await this.apiClient.get('/system/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching system status:', error);
      throw error;
    }
  }
}

export default new ApiService();